import { BLOCK_ATTRS, isValidBlockAttr } from "./blocks/registry.js";

// A extensão `.js` NÃO é opcional aqui. Este módulo é carregado pelas funções
// serverless da Vercel, que rodam ESM de verdade: import relativo de VALOR sem
// extensão não resolve e derruba a função inteira no carregamento
// (FUNCTION_INVOCATION_FAILED), não na chamada. `import type` é apagado na
// compilação e por isso não precisa — daí a mistura no arquivo.

/**
 * Política de sanitização do HTML do post — FONTE ÚNICA.
 *
 * Os dois sanitizadores (`sanitizeHtml.ts` no browser, com DOMPurify, e
 * `sanitizeHtml.server.ts` no SSR/Node, com sanitize-html) importam daqui.
 * Antes a allowlist era copiada nos dois arquivos: qualquer divergência abria
 * uma brecha em um dos caminhos sem que ninguém percebesse — é exatamente o
 * tipo de erro que um sanitizador não pode ter. Mudou aqui, muda nos dois.
 *
 * Regras ao ampliar a allowlist:
 * - Nunca adicionar tags que executam ou carregam código (`script`, `style`,
 *   `iframe`, `object`, `embed`, `form`, `input`).
 * - Nunca adicionar atributos de evento (`on*`) nem `style` (injeção de CSS).
 * - Atributo que carrega URL só com esquema na allowlist (ver ALLOWED_SCHEMES).
 * - Atributo novo entra em ATTR_BY_TAG, na tag específica — não existe
 *   atributo global.
 */

export const ALLOWED_TAGS = [
  "h2",
  "h3",
  "p",
  "ul",
  "ol",
  "li",
  "blockquote",
  "strong",
  "em",
  "a",
  "img",
  "code",
  "br",
  "figure",
  "figcaption",
  // Tabelas (E1.1). O editor usa TipTap com `resizable: false`, então não há
  // `colgroup`/`col` nem `style` de largura para permitir — a largura das
  // colunas fica por conta do CSS.
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "caption",
  // Casca dos blocos ricos (E1.2). Só `div`, e só com os atributos de bloco —
  // o conteúdo de dentro é feito das tags acima e passa pela mesma allowlist.
  "div",
];

/**
 * Atributos permitidos, por tag. Tag ausente daqui não carrega atributo
 * nenhum. Não há entrada `*`: no `sanitize-html` o curinga faz UNIÃO com a
 * lista específica da tag, o que anularia o estreitamento (um `<td href>`
 * passaria). Os dois caminhos aplicam exatamente este mapa.
 */
export const ATTR_BY_TAG: Record<string, string[]> = {
  a: ["href", "target", "rel"],
  img: ["src", "alt", "loading"],
  td: ["colspan", "rowspan"],
  th: ["colspan", "rowspan", "scope"],
  div: BLOCK_ATTRS,
};

/**
 * União dos atributos do mapa acima. O DOMPurify só aceita allowlist global de
 * atributos; o estreitamento por tag é feito por hook em `sanitizeHtml.ts`.
 */
export const ALLOWED_ATTR = [...new Set(Object.values(ATTR_BY_TAG).flat())];

export const ALLOWED_SCHEMES = ["http", "https", "mailto", "tel"];

/** Atributos cujo valor é uma URL e precisam de checagem de esquema. */
const URL_ATTRS = new Set(["href", "src"]);

/**
 * Espelha, para o DOMPurify, o que o `sanitize-html` já faz por configuração
 * (`allowedSchemes` + `allowProtocolRelative: false`):
 *
 * - URL relativa (`/blog/x`, `#secao`) passa — é o caso dos links internos.
 * - URL com esquema só passa se o esquema estiver na allowlist.
 * - `//host` (protocol-relative) não passa.
 *
 * Em particular `data:` NÃO passa em lugar nenhum, inclusive `img src`. O
 * DOMPurify libera data-URI em imagem por padrão; o SSR nunca liberou, então a
 * imagem em base64 já era descartada no HTML servido ao crawler. Manter os dois
 * iguais é o que importa — em produção as imagens vêm de URL do upload; só o
 * mock de desenvolvimento gera base64.
 */
export const isAllowedAttrValue = (attrName: string, value: string): boolean => {
  // Atributo de bloco só aceita valor do registro: tipo desconhecido perde o
  // atributo e o bloco degrada para um <div> sem estilo, mostrando o conteúdo.
  if (BLOCK_ATTRS.includes(attrName)) return isValidBlockAttr(attrName, value);

  if (!URL_ATTRS.has(attrName)) return true;

  const url = value.trim();
  if (url.startsWith("//")) return false;

  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1];
  return scheme === undefined || ALLOWED_SCHEMES.includes(scheme.toLowerCase());
};
